import { byRecency, recencyRank } from '@/components/ui/recency'
import { toNumber } from '@/lib/decimal'
import type {
  CategoryOption,
  IsoDate,
  PickerData,
  ProjectOption,
  WeekApproval,
  WeekCell,
  WeekRow,
} from '@/lib/types'
import { weekMetaFromId } from '@/lib/week'

/**
 * Het maandeinde binnen deze week, als de week over een maandgrens loopt. Null als de
 * week binnen een maand valt, en ook als de week op het maandeinde eindigt: dan is
 * "Week afsluiten" het juiste gebaar en voegt een deelafsluiting niets toe. Dezelfde
 * regel als de servervalidatie, zodat de knop alleen verschijnt waar hij werkt.
 */
export function monthEndInWeek(weekId: string): IsoDate | null {
  const { days } = weekMetaFromId(weekId)
  const monthEnd = days.find((day, index) => index < 6 && day.date.getMonth() !== days[index + 1].date.getMonth())
  return monthEnd?.iso ?? null
}

/**
 * Spiegel van WeekApproval::coversDay: zit deze dag in het afgesloten deel? Een
 * volledig akkoord dekt de hele week, een deelafsluiting alles t/m de knipdatum.
 */
export function closedOn(approval: WeekApproval | null | undefined, date: IsoDate): boolean {
  if (!approval) return false
  if (approval.status === 'APPROVED') return true
  if (approval.status === 'PARTIAL') return approval.approvedThrough !== null && date <= approval.approvedThrough
  return false
}

/** Een gekozen bestemming: waar een regel op geboekt wordt. */
export interface NewRowSelection {
  projectId: string
  clientId: string
  clientName: string
  projectName: string
  categoryId: string | null
  categoryName: string | null
  categorySortOrder: number
  categoryRequired: boolean
  fixedFee: boolean
  travelAgreement: string | null
  lastTravelAmount: string | null
}

/**
 * Lokaal toegevoegde rij; het slot onderscheidt meerdere activiteiten op hetzelfde
 * project+categorie. `dayIndex` is de dag waarop de rij is neergezet, of null als hij
 * uit het weekrooster komt en dus geen eigen dag heeft.
 */
export type ExtraRow = NewRowSelection & { slot: number; dayIndex: number | null }

/** Alle woorden moeten voorkomen; volgorde doet er niet toe. */
export function matchesTokens(haystack: string, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  const value = haystack.toLowerCase()
  return needle.split(/\s+/).every((token) => value.includes(token))
}

export interface RowMenuState {
  entryIds: string[]
  /** Nog niets opgeslagen: een wissel is dan een lokale vervanging, geen API-call. */
  isLocalRow: boolean
  canChangeClient: boolean
  canChangeProject: boolean
  canChangeCategory: boolean
}

/**
 * Wat de drie klikdoelen op deze regel mogen. Klant, project en categorie zijn elk hun
 * eigen doel: het projectmenu is daarom klant-beperkt en toont alleen projecten van de
 * eigen klant. Van klant wisselen loopt uitsluitend via de klantnaam, en die staat alleen
 * op de eerste regel van een klantblok.
 *
 * `saved` dekt het gat tussen de eerste invoer en het serverantwoord: de entry
 * bestaat dan al, maar de regel heeft nog geen entry-ids. Zou hij in dat venster
 * als lokaal gelden, dan verplaatst het menu de lege huls en blijven de zojuist
 * geboekte uren op het oude project staan, zonder melding. Wisselen kan daarom
 * pas weer als de regel zijn entry-ids heeft.
 */
export function rowMenuState(row: WeekRow, canEdit: boolean, pickers: PickerData | undefined, saved = false): RowMenuState {
  const entryIds = row.cells.map((c) => c.entryId).filter((id): id is string => id !== null)
  const locked = row.cells.some((c) => c.locked)
  const settling = entryIds.length === 0 && saved
  const base = canEdit && !locked && pickers !== undefined && !settling
  return {
    entryIds,
    isLocalRow: entryIds.length === 0 && !saved,
    canChangeClient: base && (pickers?.projects.some((p) => p.clientId !== row.clientId) ?? false),
    canChangeProject:
      base && (pickers?.projects.some((p) => p.clientId === row.clientId && p.id !== row.projectId) ?? false),
    canChangeCategory: base && (pickers?.categoriesByProject[row.projectId] ?? []).length > 0,
  }
}

export interface ClientOption {
  id: string
  name: string
  projectCount: number
  /** Laatste boeking op een van zijn projecten; bepaalt de volgorde. */
  lastBookedAt: string | null
}

/**
 * De klanten waar je uren op kunt boeken, afgeleid uit de projecten: een klant zonder
 * boekbaar project kan sowieso geen bestemming zijn. Recent geboekt eerst, daarna
 * alfabetisch: wie deze maand bij dezelfde twee klanten zit vindt ze bovenaan, en wie
 * verder kijkt heeft alsnog een voorspelbare lijst. Dit is een menu en niet de
 * rijvolgorde, dus localeCompare mag hier wel.
 */
export function clientOptions(pickers: PickerData): ClientOption[] {
  const byId = new Map<string, ClientOption>()
  for (const project of pickers.projects) {
    const known = byId.get(project.clientId)
    if (known) {
      known.projectCount += 1
      if (recencyRank(project.lastBookedAt) > recencyRank(known.lastBookedAt)) known.lastBookedAt = project.lastBookedAt
    } else {
      byId.set(project.clientId, {
        id: project.clientId,
        name: project.clientName,
        projectCount: 1,
        lastBookedAt: project.lastBookedAt,
      })
    }
  }
  return [...byId.values()].sort(byRecency((c) => c.lastBookedAt, (c) => c.name))
}

export function projectsForClient(pickers: PickerData, clientId: string): ProjectOption[] {
  return pickers.projects.filter((p) => p.clientId === clientId).sort(byRecency((p) => p.lastBookedAt, (p) => p.name))
}

/**
 * De categorieen van een project, net als de lagen erboven op recentheid. Dat wijkt af van
 * de `sortOrder` waarop de urenregels zelf staan: dit is een keuzelijst, en daar telt waar
 * je laatst op boekte zwaarder dan de vaste volgorde op de factuur.
 */
export function categoriesForProject(pickers: PickerData, projectId: string): CategoryOption[] {
  return [...(pickers.categoriesByProject[projectId] ?? [])].sort(byRecency((c) => c.lastBookedAt, (c) => c.name))
}

export type MenuStep =
  | { kind: 'client' }
  | { kind: 'project'; clientId: string; clientName: string }
  | { kind: 'category'; project: ProjectOption }

export type MenuChoice = { step: MenuStep } | { commit: { project: ProjectOption; category: CategoryOption | null } }

/**
 * Wat er gebeurt als je een klant kiest. Een urenregel moet een project hebben, dus de
 * keuze loopt door; wat vaststaat wordt overgeslagen, zodat een klant met een enkel
 * project geen lijst van een item oplevert. Ook een project dat nog een categorie mist
 * telt mee: dat loopt door naar de categoriestap, waar je er een aanmaakt.
 */
export function chooseClient(client: ClientOption, pickers: PickerData): MenuChoice {
  const projects = projectsForClient(pickers, client.id)
  if (projects.length === 1) return chooseProject(projects[0], pickers)
  return { step: { kind: 'project', clientId: client.id, clientName: client.name } }
}

export function chooseProject(project: ProjectOption, pickers: PickerData): MenuChoice {
  const categories = pickers.categoriesByProject[project.id] ?? []
  if (categories.length > 0 || project.categoryRequired) return { step: { kind: 'category', project } }
  return { commit: { project, category: null } }
}

export function selectionFromProject(project: ProjectOption, category: CategoryOption | null): NewRowSelection {
  return {
    projectId: project.id,
    clientId: project.clientId,
    clientName: project.clientName,
    projectName: project.name,
    categoryId: category?.id ?? null,
    categoryName: category?.name ?? null,
    categorySortOrder: category?.sortOrder ?? -1,
    categoryRequired: project.categoryRequired,
    fixedFee: project.fixedFee,
    travelAgreement: project.travelAgreement,
    lastTravelAmount: project.lastTravelAmount,
  }
}

/** De regel als keuze, voor een lokale categoriewissel op een nog lege regel. */
export function selectionFromRow(row: WeekRow): NewRowSelection {
  return {
    projectId: row.projectId,
    clientId: row.clientId,
    clientName: row.clientName,
    projectName: row.projectName,
    categoryId: row.categoryId,
    categoryName: row.categoryName,
    categorySortOrder: row.categorySortOrder,
    categoryRequired: row.categoryRequired,
    fixedFee: row.fixedFee,
    travelAgreement: row.travelAgreement,
    lastTravelAmount: row.lastTravelAmount,
  }
}

export function rowKeyFor(selection: { projectId: string; categoryId: string | null }, slot: number): string {
  return `${selection.projectId}::${selection.categoryId ?? ''}::${slot}`
}

/** Eerstvolgende vrije slot voor dit project+categorie, over server- en lokale rijen heen. */
export function nextSlot(rows: WeekRow[], projectId: string, categoryId: string | null): number {
  const prefix = `${projectId}::${categoryId ?? ''}::`
  const used = rows.filter((r) => r.rowKey.startsWith(prefix)).map((r) => Number(r.rowKey.slice(prefix.length)))
  return used.length > 0 ? Math.max(...used) + 1 : 0
}

/**
 * Het slot waarop de server een nieuwe boeking van deze dag neerzet. `WeekGridService`
 * nummert per dag: de boekingen van een dag vullen de slots van boven af, dus het aantal
 * regels dat die dag al een boeking op dit project heeft is precies het volgende slot.
 *
 * Dat is iets anders dan `nextSlot`, dat naar de hele week kijkt. Staan er op maandag twee
 * boekingen op dit project en op woensdag geen, dan raadt `nextSlot` slot 2 terwijl de
 * server de woensdagboeking op slot 0 zet.
 */
export function slotOnDay(rows: WeekRow[], projectId: string, categoryId: string | null, dayIndex: number): number {
  return rows.filter(
    (row) =>
      row.projectId === projectId &&
      (row.categoryId ?? '') === (categoryId ?? '') &&
      (row.cells[dayIndex]?.entryId ?? null) !== null,
  ).length
}

/**
 * De sleutels die op deze dag als lokale regel meetellen. Een regel telt mee zolang de
 * server hem nog niet kent, en alleen op de dag waar hij is neergezet. Zonder die twee
 * grenzen bleef een op maandag opgeslagen regel de rest van de week als lege 0:00-regel
 * staan: de dagweergave laat een regel immers ook zien als hij lokaal is.
 *
 * Een regel uit het weekrooster kent geen dag en telt overal mee tot hij is opgeslagen;
 * daar is geen dag om hem aan te binden.
 */
export function localKeysOnDay(extraRows: ExtraRow[], serverRows: WeekRow[], dayIndex: number): Set<string> {
  const serverKeys = new Set(serverRows.map((r) => r.rowKey))
  return new Set(
    extraRows
      .filter((r) => r.dayIndex === null || r.dayIndex === dayIndex)
      .map((r) => rowKeyFor(r, r.slot))
      .filter((key) => !serverKeys.has(key)),
  )
}

/**
 * Of deze dagcel op de dagweergave thuishoort. Een cel telt mee zodra de server hem
 * kent, maar ook als er alleen nog invoer in staat: bij het opslaan van een verse regel
 * verspringt de sleutel naar het slot van de server, en tot de refetch landt heeft die
 * cel wel de optimistisch gepatchte uren maar nog geen entryId. Op `entryId` alleen
 * verdween de regel in dat gat van het scherm en kwam hij daarna terug.
 */
export function hasDayContent(cell: WeekCell): boolean {
  return (
    cell.entryId !== null ||
    toNumber(cell.hours) > 0 ||
    toNumber(cell.travelAmount) > 0 ||
    cell.description.trim() !== ''
  )
}

/**
 * De regels die de dagweergave toont: wat op deze dag inhoud heeft, plus wat je hier net
 * hebt neergezet. Regels die alleen op een andere dag van de week uren hebben horen hier
 * niet als lege regel te staan; die komen terug als suggestie-chip.
 */
export function rowsForDay(rows: WeekRow[], localKeys: Set<string>, dayIndex: number): WeekRow[] {
  return rows.filter((row) => hasDayContent(row.cells[dayIndex]) || localKeys.has(row.rowKey))
}

/**
 * Spiegelt de sortering van WeekGridService::buildWeek. De sleutels moeten exact gelijk
 * blijven aan de PHP-kant: wijkt de lokale volgorde af, dan springen rijen zodra het
 * serverantwoord binnenkomt.
 */
/** Codepoint-vergelijking, net als PHP's `<=>` op strings. localeCompare zou bij accenten
 * een andere volgorde geven dan de server en de rijen laten springen. */
function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

export function compareRows(a: WeekRow, b: WeekRow): number {
  return (
    cmp(a.clientName.toLowerCase(), b.clientName.toLowerCase()) ||
    cmp(a.clientId, b.clientId) ||
    cmp(a.projectName.toLowerCase(), b.projectName.toLowerCase()) ||
    cmp(a.projectId, b.projectId) ||
    a.categorySortOrder - b.categorySortOrder ||
    cmp(a.categoryId ?? '', b.categoryId ?? '') ||
    a.slot - b.slot
  )
}

/** Lokaal toegevoegde rijen landen op hun definitieve plek, dus in het blok van hun eigen klant. */
export function mergeRows(serverRows: WeekRow[], extras: WeekRow[]): WeekRow[] {
  return [...serverRows, ...extras].sort(compareRows)
}

export function emptyRow(selection: ExtraRow, weekId: string): WeekRow {
  const meta = weekMetaFromId(weekId)
  return {
    rowKey: rowKeyFor(selection, selection.slot),
    slot: selection.slot,
    projectId: selection.projectId,
    clientId: selection.clientId,
    clientName: selection.clientName,
    projectName: selection.projectName,
    categoryId: selection.categoryId,
    categoryName: selection.categoryName,
    categorySortOrder: selection.categorySortOrder,
    categoryRequired: selection.categoryRequired,
    fixedFee: selection.fixedFee,
    cells: meta.days.map((day) => ({
      date: day.iso,
      dayIndex: day.dayIndex,
      entryId: null,
      hours: '0.00',
      description: '',
      travelAmount: null,
      locked: false,
      invoiceNumber: null,
    })),
    rowTotal: '0.00',
    lastUsedAt: null,
    travelAgreement: selection.travelAgreement,
    lastTravelAmount: selection.lastTravelAmount,
  }
}

export interface RowBoundary {
  /** Eerste regel van een klantblok: hier staat de klantnaam, en hierboven komt de scheiding. */
  firstOfClient: boolean
  /** Eerste regel van een project binnen hetzelfde klantblok. */
  firstOfProject: boolean
  /** Tweede of latere activiteit op hetzelfde project en dezelfde categorie. */
  isContinuation: boolean
}

/**
 * Blokgrenzen op basis van de directe voorganger. Dat mag positioneel, want de volgorde is
 * deterministisch per klant gegroepeerd. Geef de voorganger uit de *al gefilterde* lijst door:
 * de dagweergaven tonen maar een deel van de weekregels.
 */
export function rowBoundary(row: WeekRow, prev: WeekRow | undefined): RowBoundary {
  const sameClient = prev !== undefined && prev.clientId === row.clientId
  const sameProject = sameClient && prev.projectId === row.projectId
  return {
    firstOfClient: !sameClient,
    firstOfProject: !sameProject,
    isContinuation: sameProject && (prev.categoryId ?? '') === (row.categoryId ?? ''),
  }
}
